import { useState } from 'react'
import { Avatar, Badge, Typography, theme } from 'antd'
import { TeamOutlined } from '@ant-design/icons'
import { observer } from 'mobx-react-lite'
import { useStore } from '../../Stores'
import { Chat } from '../../Contracts'
import { formatRelativeTime } from '../../Helpers/formatDate'
import { truncateText } from '../../Helpers/truncateText'
import { stripHtml } from '../../Helpers/stripHtml'
import OnlineIndicator from '../../Components/common/OnlineIndicator'

const { Text } = Typography

interface ChatListItemProps {
  chat: Chat
}

const ChatListItem = observer(({ chat }: ChatListItemProps) => {
  const { uiStore, chatStore, userStore } = useStore()
  const { token } = theme.useToken()
  const [isHovered, setIsHovered] = useState(false)

  const isActive = uiStore.activeChatId === chat.id
  const displayName = chatStore.getChatDisplayName(chat)
  const avatarUrl = chatStore.getChatAvatar(chat)
  const draft = !isActive ? uiStore.getDraft(chat.id) : undefined

  // «Друкує…» витісняє і чернетку, і прев'ю (формулювання — у ChatStore.typingLabel)
  const typingLabel = chatStore.typingLabel(chat)

  // Get online status for private chats
  const isOnline = chat.type === 'private'
    ? (() => {
        const otherId = chat.participantIds.find((id: string) => id !== userStore.currentUserId)
        return otherId ? userStore.getById(otherId)?.status === 'online' : false
      })()
    : false

  const handleClick = () => {
    uiStore.setActiveChatId(chat.id)
    chatStore.markChatAsRead(chat.id)
  }

  return (
    <div
      onClick={handleClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        cursor: 'pointer',
        background: isActive ? token.colorPrimaryBg : isHovered ? token.colorFillTertiary : 'transparent',
        borderLeft: isActive ? `3px solid ${token.colorPrimary}` : '3px solid transparent',
        transition: 'background 0.2s',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Avatar with online indicator */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <Avatar
          src={avatarUrl}
          size={44}
          icon={chat.type === 'group' ? <TeamOutlined /> : undefined}
        >
          {!avatarUrl && displayName.charAt(0)}
        </Avatar>
        {isOnline && <OnlineIndicator />}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Text
            strong
            style={{
              fontSize: 14,
              lineHeight: '20px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
            }}
          >
            {displayName}
          </Text>
          <Text
            type="secondary"
            style={{ fontSize: 11, flexShrink: 0, marginLeft: 8 }}
          >
            {chat.lastMessage ? formatRelativeTime(chat.lastMessage.timestamp) : ''}
          </Text>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
          {typingLabel ? (
            <Text
              italic
              style={{
                fontSize: 13,
                lineHeight: '18px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                color: token.colorPrimary,
              }}
            >
              {typingLabel}
            </Text>
          ) : draft ? (
            <Text
              style={{
                fontSize: 13,
                lineHeight: '18px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
            >
              <span style={{ color: token.colorError, fontWeight: 500 }}>Чернетка: </span>
              <span style={{ color: token.colorTextSecondary }}>{truncateText(stripHtml(draft), 30)}</span>
            </Text>
          ) : (
            <Text
              type="secondary"
              style={{
                fontSize: 13,
                lineHeight: '18px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
            >
              {chat.lastMessage ? truncateText(chat.lastMessage.text, 40) : 'Немає повідомлень'}
            </Text>
          )}
          {chat.unreadCount > 0 && (
            <Badge
              count={chat.unreadCount}
              size="small"
              style={{
                marginLeft: 8,
                flexShrink: 0,
                // Muted chat keeps its counter but visually steps back
                ...(chat.notificationLevel === 'muted'
                  ? { backgroundColor: token.colorTextQuaternary }
                  : {}),
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
})

export default ChatListItem
